<template>
    <v-container class="fill-height my-0 py-0">
        <!-- <div class="text-lg-right">
            <v-btn color="primary" text @click="cancel">close</v-btn>
        </div> -->
        <!-- <v-card-title>
            <span class="headline">Upload Resource Files</span>
        </v-card-title> -->
        <v-row v-if="isReplacing" class="mx-0">
            <v-col cols="12">
                <div class="avenir_medium label_medium mb-8 accent--text text--darken-2">
                    <ul>
                        <li>Updating <strong>{{ replaceDoc.client_label }}</strong> ({{ replaceDoc.admin_label }}).</li>
                        <li>Make your edits and upload the file with the same file name: <strong>{{ replaceDoc.s3_file_name }}</strong></li>
                        <li>The updated file is used by every module that uses this resource. Their order, labels, visibility and active settings don't change.</li>
                    </ul>
                </div>
            </v-col>
        </v-row>
        <v-row v-else class="mx-0">
            <v-col cols="12">
                &nbsp;
            </v-col>
        </v-row>

        <v-row v-if="!isReplacing" class="mx-0">
            <v-col cols="12">
                <v-autocomplete
                    v-model="selectedModuleIds"
                    :items="moduleOptions"
                    item-text="module_name"
                    item-value="module_id"
                    item-disabled="is_current"
                    label="Apply to Modules"
                    hint="The file(s) will be added to the end of each selected module's resource list. The current module is always included."
                    persistent-hint
                    :loading="loadingModules"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    class="mb-4"
                />
            </v-col>
        </v-row>

        <v-row class="mx-0">
            <v-col cols="12">
                <v-file-input
                    v-model="files"
                    :label="labelText"
                    :hint="hintText"
                    persistent-hint
                    truncate-length="300"
                    :multiple="isMultiple"
                    :accept="allowedFileTypes.join(',')"
                    :error-messages="fileErrorMessages"
                    @change="handleChange"
                />
            </v-col>
        </v-row>
         <v-row>
            <v-col cols="12">
                <v-progress-linear v-if="isUploading"
                    :indeterminate="true"
                />
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-row>
            <v-col cols="12" class="d-flex justify-end">
                <!-- <v-btn color="accent" text @click="cancel">Cancel</v-btn> -->
                <v-btn 
                    color="primary lighten-2 info--text"
                    :disabled="isUploading || !!fileErrorMessages.length"
                    @click="upload">
                    Upload
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment';
    import { safeParseJson } from '../helpers/safe-json';

    export default {
        name: 'UploadResourceFiles',
        
        props: {
            moduleId: String,
            showReuploadMessage: Boolean,
            replaceDoc: Object, // THE RESOURCE BEING REPLACED BY A REUPLOAD - null FOR A NEW UPLOAD
            closeResourceUploads: Function,
            refreshDocs: Function
        },
        
        data: () => ({
            files: [],
            message: '',
            current_files: [],
            isUploading: false,
            disable_upload: false,
            warning_fileType: false,
            warning_fileSize: false,
            warning_fileDuplicate: false,
            invalid_filetype_details: [],
            file_match: [],
            warning_message: 'There seems to be an issue with the file being uploaded!',
            allowedFileTypes: [
                "application/octet-stream", // general
                "application/msword", // doc
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
                "application/vnd.ms-word.document.macroenabled.12", // docm on Mac
                "application/vnd.ms-word.document.macroEnabled.12", // docm on Windows
                "application/vnd.ms-excel", // xls
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
                "application/vnd.ms-excel.sheet.macroenabled.12", // xlsm on Mac
                "application/vnd.ms-excel.sheet.macroEnabled.12", // xlsm on Windows
                "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
                "application/pdf", // pdf
                "image/jpeg", // jpg
                "image/png", // png
                "video/mp4", // mp4
                "video/quicktime" // mov
            ],
            // deliverableType: null,
            // changedDeliverableType: false,
            fileTypeHint: '',
            rules: {
                required: value => !!value || 'required'
            },
            generateEmail: false,
            revisionDocCount: 2, // This will be set by the parent component
            activeModules: [],
            selectedModuleIds: [],
            loadingModules: false,
            uploadError: '',
        }),

        computed: {
            moduleOptions () {
                // CURRENT MODULE FIRST & LOCKED IN, FOLLOWED BY ALL OTHER ACTIVE MODULES
                const current = this.activeModules.find( mod => mod.module_id == this.moduleId )
                const others = this.activeModules.filter( mod => mod.module_id != this.moduleId )
                return [
                    { module_id: this.moduleId, module_name: `${current ? current.module_name : 'Current Module'} (current)`, is_current: true },
                    ...others.map( mod => ({ module_id: mod.module_id, module_name: mod.module_name, is_current: false }) )
                ]
            },

            isReplacing () {
                return !!(this.showReuploadMessage && this.replaceDoc && this.replaceDoc.file_id)
            },

            replaceNameMismatch () {
                // A REUPLOAD MUST USE THE SAME FILE NAME SO S3 OVERWRITES THE EXISTING FILE (QUOTES ARE STRIPPED ON UPLOAD)
                if ( !this.isReplacing ) return false
                const selected = Array.isArray(this.files) ? this.files[0] : this.files
                if ( !selected ) return false
                return selected.name.replace(/['"]/g, '').trim() !== this.replaceDoc.s3_file_name
            },

            fileErrorMessages () {
                if ( this.replaceNameMismatch ) return [ `The file name must be "${this.replaceDoc.s3_file_name}"` ]
                if ( this.uploadError ) return [ this.uploadError ]
                return []
            },

            isMultiple () {
                return !this.isReplacing && this.revisionDocCount > 1 ? true : false
            },
            
            hintText () {
                return this.isMultiple ? 'You can upload multiple files at once ... Accepted file types: pdf, doc/docx/docm, xls/xlsx/xlsm' : 'Upload only one file ... Accepted file types: pdf, doc/docx/docm, xls/xlsx/xlsm'
            },
            
            labelText () {
                return this.isMultiple ? 'Click to Select Files' : 'Click to Select a File'
            },

            // deliverable_types () {
            //     if ( this.moduleKey === 'entity-maintenance-program'){
            //         return this.emp_deliverable_types;
            //     }
            //     return this.standard_deliverable_types;
            // }

        },

        watch: {
            replaceDoc () {
                // NEW REUPLOAD TARGET (OR BACK TO A NORMAL UPLOAD) - CLEAR ANY PREVIOUS SELECTION & ERROR
                this.files = []
                this.uploadError = ''
            },

            moduleId: {
                immediate: true,
                handler () {
                    this.resetModuleSelection()
                }
            }
        },

        mounted: function () {
            this.getActiveModules()
        },

        methods: {
            getActiveModules: async function () {
                this.loadingModules = true
                try {
                    const all_modules = await axios.get(`${appUrls.api}/api/admin/submodule/false/all_modules`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })
                    this.activeModules = all_modules.data.outcome && Array.isArray(all_modules.data.modules) ? all_modules.data.modules : []
                } catch (err) {
                    console.log(err)
                } finally {
                    this.loadingModules = false
                }
            },

            resetModuleSelection: function () {
                this.selectedModuleIds = this.moduleId ? [ this.moduleId ] : []
            },

            fileCheck: async function (fieldData, projectId) {
                try {                    
                    const file_check = await axios.post(`${appUrls.api}/api/file_check`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        fieldData, 
                        projectId
                    })
                    
                    this.current_files = file_check.data.outcome ? safeParseJson(file_check.data.file_obj, []) : []                    
                } catch (err) {
                    console.log(err)
                    return err
                }
            },

            handleChange: function (files) {
                // console.log(files)
                // A SINGLE-FILE INPUT (REUPLOAD) RETURNS ONE File INSTEAD OF AN ARRAY
                if (files && !Array.isArray(files)) files = [files]
                this.uploadError = ''
                if (files) {
                    // console.log(files)
                    this.warning_message = 'There seems to be an issue with the file being uploaded!'
                    this.invalid_filetype_details = []
                    this.file_match = []

                    const invalid_file = files.some( file => {
                        return !this.allowedFileTypes.includes(file.type) || file.size == 0 || this.current_files.some( current => current.filename == file.name )
                    })

                    if ( !invalid_file ) {
                        // Save a reference to the file blob.
                        // We will use this later for uploading.
                        this.disable_upload = false
                        this.warning_fileType = false
                        this.warning_fileDuplicate = false
                        this.files = files
                    } else {
                        this.disable_upload = true

                        files.forEach( file => {
                            if ( !this.allowedFileTypes.includes(file.type) ) {
                                this.warning_fileType = true
                                this.invalid_filetype_details.push(file)
                            }
                            
                            if ( file.size == 0 ) {
                                this.warning_fileSize = true
                            }

                            if ( this.current_files.some( current => current.filename == file.name ) ) {
                                this.current_files.forEach( current => {
                                    if ( current.filename == file.name ) {
                                        this.file_match.push(current.filename)
                                    }
                                })
                                this.warning_fileDuplicate = true
                            }
                        })
                        
                        if ( this.invalid_filetype_details.length ) { 
                            this.warning_message = `The file type <strong>${this.invalid_filetype_details[0].type}</strong> is not permitted for uploading! Allowed file types include: pdf, doc/docx/docm, xls/xlsx/xlsm`
                        }
                            
                        if ( this.warning_fileSize ) {
                            this.warning_message = `The file size is 0mb, please check the file and upload a file with a larger size.`
                        }

                        if ( this.warning_fileDuplicate ) {
                            if ( this.file_match.length == 1 ) {
                                this.warning_message = `This file: <strong>${this.file_match}</strong> seems to be a duplicate, please delete the file before uploading the new one.`
                            } else {
                                this.warning_message = `These files: <strong>${this.file_match.join(', ')}</strong> seem to be a duplicates, please delete these files before uploading the new ones.`
                            }
                        }
                        
                        this.files = []
                    }
                }
            },

            upload: async function () {
                // this.$emit('closeResourceUploads');
                // setTimeout(() => { 
                //     this.$emit('refreshDocs');
                //     this.files=[]
                // }, 500)

                this.isUploading = true;
                
                const formData = new FormData(); // document.getElementById('form')
                const upload_files = Array.isArray(this.files) ? this.files : (this.files ? [this.files] : [])
                upload_files.forEach( file => {
                    // console.log(file)
                    return formData.append('files', file);
                })
                formData.append('authentication', Cookie.get('token'));
                formData.append('moduleId', this.moduleId);
                if ( this.isReplacing ) {
                    // REUPLOAD - REPLACES THE FILE FOR EVERY MODULE ALREADY USING IT, SO NO MODULE SELECTION
                    formData.append('replaceDocId', this.replaceDoc.file_id);
                } else {
                    formData.append('moduleIds', JSON.stringify(this.selectedModuleIds.filter( id => id != this.moduleId )));
                }
                formData.append('source', 'admin');
                // console.log(...formData)

                try {
                    const res = await axios.post(`${appUrls.api}/api/upload_resource_file/${this.moduleId}`, formData)
                    
                    if ( res.data.outcome === false ) {
                        throw new Error(res.data.message);
                    }

                    this.$emit('closeResourceUploads');
                    this.$emit('showReuploadMessage', false);
                    this.isUploading = false;
                    this.file = null;
                    this.files = null;
                    this.resetModuleSelection();
                    // this.$emit('triggerUserFeedback', true, 'File(s) successfully uploaded!' );

                    this.$emit('refreshDocs');

                } catch (err) {
                    if ( this.isReplacing ) {
                        // KEEP THE DRAWER OPEN SO THE ADMIN CAN SEE WHY THE REUPLOAD FAILED
                        this.uploadError = err.response && err.response.data && err.response.data.message ? err.response.data.message : err.message
                        this.isUploading = false;
                        return
                    }
                    setTimeout(() => {
                        this.$emit('closeResourceUploads');
                        this.$emit('showReuploadMessage', false);
                        this.isUploading = false;
                        this.file = null;
                        this.files = null;
                        // this.$emit('triggerUserFeedback', true, err.message );
                    }, 2000)
                }
            },

            cancel: function () {
                // Reset the control on cancel so it loads fresh next time
                this.$emit('showReuploadMessage', false);
                this.isUploading = false;
                this.files = null;
                this.resetModuleSelection();

                this.$emit('closeResourceUploads');
            }
        }
    }
</script>

<style scoped>
    .required_rule >>> .v-messages__message {
        color:red;
    }
</style>


// WEBPACK FOOTER //
// src/components/UploadResourceFiles.vue